// src/components/feature/EsaHousingGuidesSection.tsx
//
// PAWTENANT-ESA-HOUSING-GUIDES-PUBLISH-001
//
// The compact list of the three ESA housing guides. Rendered at the foot of
// each guide ("Related ESA housing guides", minus the guide being read) and on
// /esa-letter-housing below the final CTA ("ESA housing resources", all three).
//
// Titles, descriptions and routes come from src/seo/blog/esaHousingGuides.ts —
// the description on each card IS the guide's approved meta description, so a
// listing can never describe a guide differently from its own <head>.
// Plain <Link>s, so the prerendered HTML carries crawlable hrefs.

import { Link } from "react-router-dom";
import { ESA_HOUSING_GUIDES, ESA_HOUSING_GUIDE_ROUTES } from "@/seo/blog/esaHousingGuides";

export default function EsaHousingGuidesSection({
  heading,
  intro,
  currentRoute,
}: {
  heading: string;
  intro?: string;
  /** The guide being read, left out of its own related list. */
  currentRoute?: string;
}) {
  const routes = ESA_HOUSING_GUIDE_ROUTES.filter((r) => r !== currentRoute);
  return (
    <section
      aria-labelledby="esa-housing-guides-heading"
      data-pt-housing-guides
      className="py-12 md:py-14 bg-[#fdf6ee] border-t border-orange-100"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-6">
        <h2
          id="esa-housing-guides-heading"
          className="text-xl md:text-2xl font-extrabold text-gray-900 mb-2 text-center"
        >
          {heading}
        </h2>
        {intro ? (
          <p className="text-sm text-gray-600 leading-relaxed text-center max-w-2xl mx-auto mb-7">{intro}</p>
        ) : (
          <div className="mb-7" />
        )}
        <div className={`grid gap-4 items-stretch ${routes.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
          {routes.map((route) => {
            const g = ESA_HOUSING_GUIDES[route];
            return (
              <Link
                key={route}
                to={route}
                className="group flex h-full flex-col bg-white border border-orange-100 rounded-2xl p-5 hover:border-orange-300 hover:shadow-sm transition-all"
              >
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-orange-50 mb-3">
                  <i className={`${g.icon} text-orange-500 text-lg`}></i>
                </span>
                <span className="text-sm font-bold text-gray-900 group-hover:text-orange-600 transition-colors leading-snug mb-2">
                  {g.breadcrumb}
                </span>
                <span className="text-xs text-gray-500 leading-relaxed flex-1">{g.metaDescription}</span>
                <span className="mt-4 text-xs font-semibold text-orange-600 inline-flex items-center gap-1">
                  Read the guide <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
